import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { controlToastState } from "risa-oasis-ui_v2";
import { FirestoreService } from "../../../api/firebase/firestoreService";
import {
  FirestoreCollectionReference,
  FirestoreDocumentReference,
} from "../../../api/firebase/references";
import { getFaxStatus } from "../../../api/postCall/getFaxStatus";
import { FaxOutputDocModel } from "../../../data-model/faxOutputDocModel";
import { RootState } from "../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { getCorrectStatusBasedOnFaxSubmissionStatus } from "../../finalDocuments/utils/faxSubmissionHelper";

interface UseRefreshFaxStatusProps {
  orderId: string | undefined;
}

export interface FaxStatusState {
  data?: string;
  loading?: boolean;
  error?: string;
}

export const useRefreshFaxStatus = ({ orderId }: UseRefreshFaxStatusProps) => {
  const [faxStatusState, setFaxStatusState] = useState<FaxStatusState>({
    data: undefined,
    loading: false,
    error: undefined,
  });
  const orgId = getOrgIdForFetchExternalWorklist();
  const { singleMedicalPaOrder } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );
  const { data: singleMedicalPaFormConfig } = useSelector(
    (state: RootState) => state.singleMedicalPaFormConfig,
  );

  const refreshFaxStatus = useCallback(async () => {
    if (!orderId) {
      controlToastState("order-id-not-found");
      setFaxStatusState((prev) => ({
        ...prev,
        error: "Order ID not found",
        loading: false,
      }));
      return;
    }

    try {
      setFaxStatusState((prev) => ({
        ...prev,
        loading: true,
      }));

      // Step 1: Get fax_details_id and form_id from submission_info/output
      const faxInfo = await FirestoreService.getDocument<FaxOutputDocModel>(
        FirestoreDocumentReference.getFaxInfoPath(orderId),
      );

      const faxDetailsId = faxInfo?.fax_details_id ?? "";
      // Try to get form_id from faxInfo first, fallback to singleMedicalPaFormConfig
      const formId = faxInfo?.form_id ?? "";

      if (!faxDetailsId || !formId) {
        controlToastState("fax-id-not-found");
        setFaxStatusState((prev) => ({
          ...prev,
          error: "Fax ID not found",
          loading: false,
        }));
        return;
      }

      // Step 2: Call getFaxStatus API
      const faxStatusResponse = await getFaxStatus({
        fax_details_id: faxDetailsId,
        form_id: formId,
        org_id: orgId ?? "",
      });

      // Step 3: Update Firestore with the new status
      await Promise.all([
        FirestoreService.updateDocument(
          `medical_pa_orders/${orderId}/submission_info/`,
          "output",
          {
            fax_status: getCorrectStatusBasedOnFaxSubmissionStatus(
              faxStatusResponse.fax_status?.sent_status ?? "",
            ),
            fax_status_updated_at: new Date().toISOString(),
            submission: getCorrectStatusBasedOnFaxSubmissionStatus(
              faxStatusResponse.fax_status?.sent_status ?? "",
            ),
          },
        ),
        FirestoreService.updateDocument(
          FirestoreCollectionReference.medicalPaOrders(),
          singleMedicalPaOrder?.id ?? "",
          {
            "status.fax_submission_status":
              getCorrectStatusBasedOnFaxSubmissionStatus(
                faxStatusResponse.fax_status?.sent_status ?? "",
              ),
            "status.submission": getCorrectStatusBasedOnFaxSubmissionStatus(
              faxStatusResponse.fax_status?.sent_status ?? "",
            ),
          },
        ),
      ]);

      // Step 4: Update the state with the response data
      setFaxStatusState((prev) => ({
        ...prev,
        data: faxStatusResponse.fax_status?.sent_status ?? "",
        loading: false,
        error: undefined,
      }));
      controlToastState("fax-status-refreshed-successfully");
    } catch (error) {
      console.error("Error refreshing fax status:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      //   setError(errorMessage);
      setFaxStatusState((prev) => ({
        ...prev,
        error: errorMessage,
        loading: false,
      }));
      controlToastState("error-getting-fax-status");
    } finally {
      setFaxStatusState((prev) => ({
        ...prev,
        loading: false,
      }));
    }
  }, [orderId, orgId, singleMedicalPaOrder?.id, singleMedicalPaFormConfig?.id]);

  return {
    refreshFaxStatus,
    faxStatusState,
    setFaxStatusState,
  };
};
